// AddOnManagement.tsx
// Admin Component for managing additional services (Zusatzoptionen)
// 5 Categories: Gastronomie, Übernachtung, Business Services, Events, Transport

import React, { useState, useEffect } from 'react';
import { formatPrice } from '../../utils/formatters';
import {
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  XCircleIcon,
  CurrencyEuroIcon,
  ClockIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import {
  adminService,
  AddOnCategory,
  AddOnService,
  CreateAddOnCategoryDto,
  CreateAddOnServiceDto,
  CompositeAddOnComponent
} from '../../services/adminService';
import './CustomerManagement.css'; // Reuse customer management styles
import { CompositionList } from './CompositionList';
import { AddCompositionDialog } from './AddCompositionDialog';

const AddOnManagement: React.FC = () => {
  const [categories, setCategories] = useState<AddOnCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<AddOnCategory | null>(null);
  const [selectedService, setSelectedService] = useState<AddOnService | null>(null);

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showServiceDetails, setShowServiceDetails] = useState(false);
  const [showCompositionDialog, setShowCompositionDialog] = useState(false);

  // Filter states
  const [includeInactive, setIncludeInactive] = useState(false);
  const [activeCategoryTab, setActiveCategoryTab] = useState<number | null>(null);

  // Composite Add-ons states (Phase 2)
  const [compositions, setCompositions] = useState<CompositeAddOnComponent[]>([]);
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [priceMode, setPriceMode] = useState<'auto' | 'manual'>('manual');
  const [isEditMode, setIsEditMode] = useState(false);

  // BRUTTO-FIRST: priceInputMode is no longer needed - always use gross price

  // Tax breakdown state (for Details view)
  const [taxBreakdown, setTaxBreakdown] = useState<import('../../services/adminService').CalculatedPriceWithTaxDto | null>(null);

  // Form state for category
  const [categoryFormData, setCategoryFormData] = useState<CreateAddOnCategoryDto>({
    name: '',
    description: '',
    iconClass: '',
    sortOrder: 1,
    isActive: true,
    isVisibleToCustomers: true,
    requiresManualApproval: false
  });

  // Form state for service - BRUTTO-FIRST: Admin enters grossPrice
  const [serviceFormData, setServiceFormData] = useState<CreateAddOnServiceDto>({
    categoryId: 0,
    name: '',
    description: '',
    grossPrice: 0.01, // BRUTTO-FIRST: Admin enters gross price
    pricingType: 'Fixed',
    taxRate: 0.19,
    isActive: true,
    sortOrder: 1,
    minQuantityPerBooking: 1,
    requiresManualConfirmation: false,
    isComposite: false
  });

  useEffect(() => {
    fetchCategories();
  }, [includeInactive]);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getAddOnCategories(includeInactive);
      setCategories(data);

      // Set first category as active tab if not set
      if (data.length > 0 && activeCategoryTab === null) {
        setActiveCategoryTab(data[0].id);
      }
    } catch (err: any) {
      console.error('Error fetching AddOn categories:', err);
      setError('Fehler beim Laden der Zusatzoptionen. Bitte Backend prüfen.');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (selectedCategory) {
        // Update existing category
        await adminService.updateAddOnCategory(selectedCategory.id, categoryFormData);
        alert('Kategorie erfolgreich aktualisiert');
      } else {
        // Create new category
        await adminService.createAddOnCategory(categoryFormData);
        alert('Neue Kategorie erfolgreich angelegt');
      }

      setShowCategoryModal(false);
      fetchCategories();
    } catch (err: any) {
      console.error('Error saving category:', err);
      alert('Fehler beim Speichern der Kategorie: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (window.confirm('Möchten Sie diese Kategorie wirklich löschen?')) {
      try {
        await adminService.deleteAddOnCategory(categoryId);
        alert('Kategorie erfolgreich gelöscht');
        fetchCategories();
      } catch (err: any) {
        console.error('Error deleting category:', err);
        alert('Fehler beim Löschen der Kategorie: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const handleSaveService = async () => {
    try {
      if (selectedService) {
        // Update existing service
        await adminService.updateAddOnService(selectedService.id, serviceFormData);
        alert('Service erfolgreich aktualisiert');
      } else {
        // Create new service
        await adminService.createAddOnService(serviceFormData);
        alert('Neuer Service erfolgreich angelegt');
      }

      setShowServiceModal(false);
      fetchCategories();
    } catch (err: any) {
      console.error('Error saving service:', err);
      alert('Fehler beim Speichern des Service: ' + (err.response?.data?.message || err.message));
    }
  };

  // Auto-save before opening composition dialog
  const handleOpenCompositionDialog = async () => {
    try {
      let updatedService: AddOnService;

      // If add-on is not saved yet or has changes, save it first
      if (!selectedService || !selectedService.id) {
        // Create new service first
        updatedService = await adminService.createAddOnService(serviceFormData);
        setSelectedService(updatedService);
        alert('Add-on wurde automatisch gespeichert');
        await fetchCategories();
      } else {
        // Update existing service to ensure isComposite flag is saved
        updatedService = await adminService.updateAddOnService(selectedService.id, serviceFormData);
        setSelectedService(updatedService);
      }

      // Verify that the add-on is marked as composite
      if (!serviceFormData.isComposite) {
        alert('Bitte aktivieren Sie erst die Checkbox "Ist Composite-Add-on"');
        return;
      }

      // Now open the dialog
      setShowCompositionDialog(true);
    } catch (err: any) {
      console.error('Error auto-saving service:', err);
      alert('Fehler beim Speichern: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (window.confirm('Möchten Sie diesen Service wirklich löschen?')) {
      try {
        await adminService.deleteAddOnService(serviceId);
        alert('Service erfolgreich gelöscht');
        fetchCategories();
      } catch (err: any) {
        console.error('Error deleting service:', err);
        alert('Fehler beim Löschen des Service: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // ========================================
  // COMPOSITE ADD-ONS CRUD OPERATIONS (Phase 2)
  // ========================================

  // Lade Komponenten für ein Composite Add-on
  const fetchCompositions = async (addOnId: number) => {
    try {
      const comps = await adminService.getCompositions(addOnId);
      setCompositions(comps);
    } catch (error) {
      console.error('Failed to fetch compositions:', error);
      setCompositions([]);
    }
  };

  // Lade berechneten Preis mit Tax-Breakdown
  const fetchCalculatedPrice = async (addOnId: number) => {
    try {
      const response = await adminService.getCalculatedPriceWithTax(addOnId);
      setCalculatedPrice(response.calculatedPrice);
      // Auch die Tax-Breakdown-Informationen für die Edit-Ansicht speichern
      setTaxBreakdown(response);
    } catch (error) {
      console.error('Failed to fetch calculated price:', error);
      setCalculatedPrice(null);
      setTaxBreakdown(null);
    }
  };

  // Komponente hinzufügen
  const handleAddComposition = async (childAddOnId: number, quantity: number) => {
    if (!selectedService || !selectedService.id) {
      alert('Bitte speichern Sie zuerst das Add-on, bevor Sie Komponenten hinzufügen');
      return;
    }

    try {
      const newComp = await adminService.addComposition(selectedService.id, {
        childAddOnId,
        quantity,
        sortOrder: compositions.length
      });

      setCompositions([...compositions, newComp]);
      await fetchCalculatedPrice(selectedService.id);
      alert('Komponente erfolgreich hinzugefügt');
    } catch (error: any) {
      alert('Fehler beim Hinzufügen: ' + (error.response?.data?.message || error.message));
      throw error;
    }
  };

  // Komponente aktualisieren (Menge ändern)
  const handleUpdateComposition = async (composition: CompositeAddOnComponent) => {
    if (!selectedService || !selectedService.id || !composition.id) return;

    try {
      const updated = await adminService.updateComposition(
        selectedService.id,
        composition.id,
        {
          quantity: composition.quantity,
          sortOrder: composition.sortOrder
        }
      );

      setCompositions(compositions.map(c =>
        c.id === composition.id ? updated : c
      ));

      await fetchCalculatedPrice(selectedService.id);
      alert('Komponente aktualisiert');
    } catch (error: any) {
      alert('Fehler beim Aktualisieren: ' + (error.response?.data?.message || error.message));
    }
  };

  // Komponente löschen
  const handleDeleteComposition = async (composition: CompositeAddOnComponent) => {
    if (!selectedService || !selectedService.id || !composition.id) return;

    if (!window.confirm(`Möchten Sie "${composition.childName}" wirklich entfernen?`)) {
      return;
    }

    try {
      await adminService.deleteComposition(selectedService.id, composition.id);
      setCompositions(compositions.filter(c => c.id !== composition.id));
      await fetchCalculatedPrice(selectedService.id);
      alert('Komponente entfernt');
    } catch (error: any) {
      alert('Fehler beim Löschen: ' + (error.response?.data?.message || error.message));
    }
  };

  // Komponenten neu sortieren
  const handleReorderCompositions = async (
    composition: CompositeAddOnComponent,
    direction: 'up' | 'down'
  ) => {
    const currentIndex = compositions.findIndex(c => c.id === composition.id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= compositions.length) return;

    // Lokale Liste neu sortieren
    const newCompositions = [...compositions];
    const [moved] = newCompositions.splice(currentIndex, 1);
    newCompositions.splice(newIndex, 0, moved);

    // SortOrder aktualisieren
    const updatedCompositions = newCompositions.map((c, index) => ({
      ...c,
      sortOrder: index
    }));

    setCompositions(updatedCompositions);

    // Backend aktualisieren (nur die beiden betroffenen)
    if (!selectedService || !selectedService.id) return;

    try {
      await Promise.all([
        adminService.updateComposition(selectedService.id, moved.id!, {
          quantity: moved.quantity,
          sortOrder: newIndex
        }),
        adminService.updateComposition(selectedService.id, newCompositions[currentIndex].id!, {
          quantity: newCompositions[currentIndex].quantity,
          sortOrder: currentIndex
        })
      ]);
    } catch (error: any) {
      alert('Fehler beim Sortieren');
      // Bei Fehler: Liste neu laden
      if (selectedService.id) {
        fetchCompositions(selectedService.id);
      }
    }
  };

  const getPricingTypeLabel = (type: string) => {
    switch(type) {
      case 'PerPerson': return 'Pro Person';
      case 'PerGroup': return 'Pro Gruppe';
      case 'PerDay': return 'Pro Tag';
      case 'PerNight': return 'Pro Nacht';
      case 'Fixed': return 'Festpreis';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="customer-management loading">
        <div className="spinner">
          <ArrowPathIcon className="w-8 h-8 animate-spin" />
          <p>Lade Zusatzoptionen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-management error">
        <p className="error-message">{error}</p>
        <button onClick={fetchCategories} className="btn-retry">
          <ArrowPathIcon className="w-5 h-5" />
          Erneut versuchen
        </button>
      </div>
    );
  }

  const activeCategory = categories.find(c => c.id === activeCategoryTab);
  const services = activeCategory?.services || [];

  return (
    <div className="customer-management">
      {/* Header */}
      <div className="customer-header">
        <div className="header-top">
          <h2>Zusatzoptionen Verwaltung</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                setSelectedCategory(null);
                setCategoryFormData({
                  name: '',
                  description: '',
                  iconClass: '',
                  sortOrder: categories.length + 1,
                  isActive: true,
                  isVisibleToCustomers: true,
                  requiresManualApproval: false
                });
                setShowCategoryModal(true);
              }}
              className="btn-secondary"
              style={{ padding: '10px 15px' }}
            >
              <PlusCircleIcon className="w-5 h-5" />
              Neue Kategorie
            </button>
            <button
              onClick={() => {
                if (!activeCategoryTab) {
                  alert('Bitte wählen Sie zuerst eine Kategorie aus');
                  return;
                }
                setSelectedService(null);
                setServiceFormData({
                  categoryId: activeCategoryTab,
                  name: '',
                  description: '',
                  grossPrice: 0.01, // BRUTTO-FIRST
                  pricingType: 'Fixed',
                  taxRate: 0.19,
                  isActive: true,
                  sortOrder: services.length + 1,
                  minQuantityPerBooking: 1,
                  requiresManualConfirmation: false,
                  isComposite: false
                });
                setShowServiceModal(true);
              }}
              className="btn-new-customer"
            >
              <PlusCircleIcon className="w-5 h-5" />
              Neuer Service
            </button>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '2rem',
        borderBottom: '2px solid #e0e0e0',
        paddingBottom: '10px',
        flexWrap: 'wrap'
      }}>
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => setActiveCategoryTab(category.id)}
            style={{
              padding: '10px 20px',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              background: activeCategoryTab === category.id ? '#007bff' : '#f0f0f0',
              color: activeCategoryTab === category.id ? 'white' : '#333',
              fontWeight: activeCategoryTab === category.id ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>{category.name}</span>
            {!category.isActive && <XCircleIcon className="w-4 h-4" style={{ color: '#dc3545' }} />}
          </button>
        ))}
      </div>

      {/* Active Category Header with Actions */}
      {activeCategory && (
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '2px solid #e9ecef'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>{activeCategory.name}</h3>
              <p style={{ margin: '8px 0 0 0', color: '#666' }}>{activeCategory.description}</p>
              <div style={{ display: 'flex', gap: '15px', marginTop: '10px', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '12px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  background: activeCategory.isActive ? '#d4edda' : '#f8d7da',
                  color: activeCategory.isActive ? '#155724' : '#721c24'
                }}>
                  {activeCategory.isActive ? 'Aktiv' : 'Inaktiv'}
                </span>
                {activeCategory.isVisibleToCustomers && (
                  <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', background: '#cce5ff', color: '#004085' }}>
                    Für Kunden sichtbar
                  </span>
                )}
                {activeCategory.requiresManualApproval && (
                  <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', background: '#fff3cd', color: '#856404' }}>
                    Manuelle Freigabe erforderlich
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  setSelectedCategory(activeCategory);
                  setCategoryFormData({
                    name: activeCategory.name,
                    description: activeCategory.description,
                    iconClass: activeCategory.iconClass,
                    sortOrder: activeCategory.sortOrder,
                    isActive: activeCategory.isActive,
                    isVisibleToCustomers: activeCategory.isVisibleToCustomers,
                    requiresManualApproval: activeCategory.requiresManualApproval
                  });
                  setShowCategoryModal(true);
                }}
                className="btn-action btn-edit"
                title="Kategorie bearbeiten"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteCategory(activeCategory.id)}
                className="btn-action btn-delete"
                title="Kategorie löschen"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Services Grid */}
      <div className="customers-grid">
        {services.length === 0 ? (
          <div className="no-data">
            <PlusCircleIcon style={{ width: '48px', height: '48px', color: '#9ca3af' }} />
            <p>Keine Services in dieser Kategorie</p>
            <button
              onClick={() => {
                setSelectedService(null);
                setServiceFormData({
                  categoryId: activeCategoryTab!,
                  name: '',
                  description: '',
                  grossPrice: 0.01, // BRUTTO-FIRST
                  pricingType: 'Fixed',
                  taxRate: 0.19,
                  isActive: true,
                  sortOrder: 1,
                  minQuantityPerBooking: 1,
                  requiresManualConfirmation: false,
                  isComposite: false
                });
                setShowServiceModal(true);
              }}
              className="btn-primary"
              style={{ marginTop: '1rem' }}
            >
              Ersten Service hinzufügen
            </button>
          </div>
        ) : (
          services.map(service => (
            <div key={service.id} className="customer-card">
              <div className="customer-card-header">
                <div className="customer-info">
                  <h3>{service.name}</h3>
                  {/* Badges for special add-on types */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                    {/* Composite Badge */}
                    {service.isComposite && (
                      <>
                        <span style={{
                          fontSize: '11px',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          background: '#007bff',
                          color: 'white',
                          fontWeight: 'bold',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <CubeIcon style={{ width: '14px', height: '14px' }} />
                          Composite
                        </span>
                        {service.compositions && service.compositions.length > 0 && (
                          <span style={{
                            fontSize: '11px',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: '#f0f0f0',
                            color: '#666'
                          }} title={`Komponenten: ${service.compositions.map(c => c.childName).join(', ')}`}>
                            {service.compositions.length} Komponente{service.compositions.length !== 1 ? 'n' : ''}
                          </span>
                        )}
                      </>
                    )}
                    {/* Optional Choice Badge */}
                    {service.isOptionalChoice && (
                      <span style={{
                        fontSize: '11px',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        background: '#059669',
                        color: 'white',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        🍽️ Optionale Auswahl
                      </span>
                    )}
                  </div>
                </div>
                <div className="customer-badges">
                  {service.isActive ? (
                    <span className="status-badge status-active">Aktiv</span>
                  ) : (
                    <span className="status-badge status-inactive">Inaktiv</span>
                  )}
                </div>
              </div>

              <div className="customer-card-body">
                <p style={{
                  fontSize: '14px',
                  color: '#666',
                  marginBottom: '1rem',
                  minHeight: '40px'
                }}>{service.description}</p>

                <div className="customer-stats">
                  <div className="stat-item">
                    <CurrencyEuroIcon className="w-4 h-4" />
                    <span>{formatPrice(service.grossPrice)} {getPricingTypeLabel(service.pricingType)}</span>
                  </div>
                  {service.minQuantityPerBooking && (
                    <div className="stat-item">
                      <UserGroupIcon className="w-4 h-4" />
                      <span>Min: {service.minQuantityPerBooking}</span>
                    </div>
                  )}
                  {service.requiredLeadTimeHours && (
                    <div className="stat-item">
                      <ClockIcon className="w-4 h-4" />
                      <span>Vorlauf: {service.requiredLeadTimeHours}h</span>
                    </div>
                  )}
                  {service.providerName && (
                    <div className="stat-item">
                      <BuildingOfficeIcon className="w-4 h-4" />
                      <span>{service.providerName}</span>
                    </div>
                  )}
                </div>

                {service.requiresManualConfirmation && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px',
                    background: '#fff3cd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#856404'
                  }}>
                    ⚠️ Manuelle Bestätigung erforderlich
                  </div>
                )}
              </div>

              <div className="customer-card-footer">
                <button
                  onClick={async () => {
                    setSelectedService(service);
                    // Load tax breakdown for composite add-ons
                    if (service.isComposite && service.id) {
                      try {
                        const priceWithTax = await adminService.getCalculatedPriceWithTax(service.id);
                        setTaxBreakdown(priceWithTax);
                        // Also attach compositions to service for backward compatibility
                        service.compositions = priceWithTax.components;
                      } catch (error) {
                        console.error('Failed to load tax breakdown for details view:', error);
                        setTaxBreakdown(null);
                      }
                    } else {
                      setTaxBreakdown(null);
                    }
                    setShowServiceDetails(true);
                  }}
                  className="btn-action btn-view"
                  title="Details anzeigen"
                >
                  Details
                </button>
                <button
                  onClick={async () => {
                    setSelectedService(service);
                    setIsEditMode(true);
                    setServiceFormData({
                      categoryId: service.categoryId || activeCategoryTab!,
                      name: service.name,
                      description: service.description,
                      grossPrice: service.grossPrice, // BRUTTO-FIRST
                      pricingType: service.pricingType,
                      taxRate: service.taxRate,
                      isActive: service.isActive,
                      sortOrder: service.sortOrder,
                      minQuantityPerBooking: service.minQuantityPerBooking,
                      maxQuantityPerBooking: service.maxQuantityPerBooking,
                      availableCapacity: service.availableCapacity,
                      requiresManualConfirmation: service.requiresManualConfirmation,
                      requiredLeadTimeHours: service.requiredLeadTimeHours,
                      providerName: service.providerName,
                      providerContact: service.providerContact,
                      providerCost: service.providerCost,
                      isComposite: service.isComposite || false,
                      isOptionalChoice: service.isOptionalChoice || false
                    });

                    // Load compositions if composite
                    if (service.isComposite && service.id) {
                      await fetchCompositions(service.id);
                      await fetchCalculatedPrice(service.id);
                    } else {
                      setCompositions([]);
                      setCalculatedPrice(null);
                    }

                    setShowServiceModal(true);
                  }}
                  className="btn-action btn-edit"
                  title="Bearbeiten"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteService(service.id)}
                  className="btn-action btn-delete"
                  title="Löschen"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Category Edit/Create Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal-content customer-form" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</h3>
              <button onClick={() => setShowCategoryModal(false)} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleSaveCategory(); }}>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={categoryFormData.name}
                      onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                      required
                      placeholder="z.B. 🍺 Gastronomie"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Beschreibung *</label>
                    <textarea
                      value={categoryFormData.description}
                      onChange={(e) => setCategoryFormData({...categoryFormData, description: e.target.value})}
                      required
                      rows={3}
                      placeholder="Beschreiben Sie diese Kategorie..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Icon-Klasse (optional)</label>
                    <input
                      type="text"
                      value={categoryFormData.iconClass || ''}
                      onChange={(e) => setCategoryFormData({...categoryFormData, iconClass: e.target.value})}
                      placeholder="fa-utensils"
                    />
                  </div>

                  <div className="form-group">
                    <label>Sortierung</label>
                    <input
                      type="number"
                      value={categoryFormData.sortOrder || 1}
                      onChange={(e) => setCategoryFormData({...categoryFormData, sortOrder: parseInt(e.target.value)})}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={categoryFormData.isActive}
                        onChange={(e) => setCategoryFormData({...categoryFormData, isActive: e.target.checked})}
                      />
                      Aktiv
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={categoryFormData.isVisibleToCustomers}
                        onChange={(e) => setCategoryFormData({...categoryFormData, isVisibleToCustomers: e.target.checked})}
                      />
                      Für Kunden sichtbar
                    </label>
                  </div>

                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={categoryFormData.requiresManualApproval}
                        onChange={(e) => setCategoryFormData({...categoryFormData, requiresManualApproval: e.target.checked})}
                      />
                      Manuelle Freigabe erforderlich
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {selectedCategory ? 'Speichern' : 'Kategorie anlegen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCategoryModal(false)}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Service Edit/Create Modal */}
      {showServiceModal && (
        <div className="modal-overlay" onClick={() => setShowServiceModal(false)}>
          <div className="modal-content customer-form" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header" style={{ background: '#2d3748', padding: '1rem' }}>
              <h3 style={{ color: 'white', margin: 0 }}>{selectedService ? 'Service bearbeiten' : 'Neuer Service'}</h3>
              <button onClick={() => setShowServiceModal(false)} className="btn-close" style={{ color: 'white' }}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleSaveService(); }}>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={serviceFormData.name}
                      onChange={(e) => setServiceFormData({...serviceFormData, name: e.target.value})}
                      required
                      placeholder="z.B. Premium Bierverkostung"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Beschreibung *</label>
                    <textarea
                      value={serviceFormData.description}
                      onChange={(e) => setServiceFormData({...serviceFormData, description: e.target.value})}
                      required
                      rows={3}
                      placeholder="Beschreiben Sie diesen Service..."
                      style={{ color: '#1a202c', background: 'white' }}
                    />
                  </div>

                  {/* NEW: Composite Add-on Checkbox */}
                  <div className="form-group full-width" style={{ marginBottom: '1.5rem' }}>
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={serviceFormData.isComposite || false}
                        onChange={async (e) => {
                          setServiceFormData({...serviceFormData, isComposite: e.target.checked});
                          if (e.target.checked) {
                            // Beim Aktivieren: Auf "Automatisch berechnen" umschalten + Komponenten neu laden
                            setPriceMode('auto');
                            // Komponenten vom Backend neu laden, falls Add-on bereits gespeichert ist
                            if (selectedService && selectedService.id) {
                              await fetchCompositions(selectedService.id);
                              await fetchCalculatedPrice(selectedService.id);
                            }
                          } else {
                            // Beim Deaktivieren: Komponenten nur visuell zurücksetzen (nicht löschen im Backend)
                            setCompositions([]);
                            setCalculatedPrice(null);
                            setPriceMode('manual');
                          }
                        }}
                        disabled={!isEditMode && !selectedService}
                      />
                      <CubeIcon style={{ width: '20px', height: '20px' }} />
                      <span>Ist Composite-Add-on (besteht aus mehreren Komponenten)</span>
                    </label>
                    {!isEditMode && !selectedService && (
                      <p style={{ fontSize: '12px', color: '#856404', background: '#fff3cd', padding: '8px', borderRadius: '4px', marginTop: '8px' }}>
                        ⚠️ Hinweis: Speichern Sie zuerst das Add-on, bevor Sie Komponenten hinzufügen können.
                      </p>
                    )}
                  </div>

                  {/* Optional Choice Add-on Checkbox */}
                  <div className="form-group full-width" style={{ marginBottom: '1.5rem' }}>
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={serviceFormData.isOptionalChoice || false}
                        onChange={(e) => {
                          setServiceFormData({...serviceFormData, isOptionalChoice: e.target.checked});
                        }}
                      />
                      <span style={{ color: '#059669', fontWeight: 'bold' }}>🍽️</span>
                      <span>Optionale Auswahl (z.B. Grillgericht-Auswahl)</span>
                    </label>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', marginLeft: '28px' }}>
                      Bei aktivierter Option muss der Kunde für alle Teilnehmer eine Auswahl treffen.
                      Die Summe aller Auswahlen muss der Teilnehmerzahl entsprechen.
                    </p>
                  </div>

                  {/* Composite Components Section */}
                  {serviceFormData.isComposite && selectedService && selectedService.id && (
                    <div className="form-group full-width" style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px', border: '2px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>Komponenten</h4>
                        <button
                          type="button"
                          onClick={handleOpenCompositionDialog}
                          style={{
                            padding: '8px 16px',
                            background: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <PlusCircleIcon style={{ width: '18px', height: '18px' }} />
                          Komponente hinzufügen
                        </button>
                      </div>

                      <CompositionList
                        compositions={compositions}
                        onUpdate={handleUpdateComposition}
                        onDelete={handleDeleteComposition}
                        onReorder={handleReorderCompositions}
                      />

                      {/* Gesamt-Komponentenpreis mit Tax-Breakdown */}
                      {taxBreakdown && taxBreakdown.taxBreakdown && taxBreakdown.taxBreakdown.length > 0 && (
                        <div style={{
                          marginTop: '1.5rem',
                          padding: '1rem',
                          background: priceMode === 'manual'
                            ? '#e0e0e0'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '8px',
                          color: priceMode === 'manual' ? '#666' : 'white',
                          opacity: priceMode === 'manual' ? 0.6 : 1
                        }}>
                          {/* Warning bei manuellem Preis */}
                          {priceMode === 'manual' && (
                            <div style={{
                              backgroundColor: '#ff9800',
                              color: 'white',
                              padding: '6px 10px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              marginBottom: '10px',
                              borderRadius: '4px',
                              opacity: 1
                            }}>
                              Wird bei manueller Preisgestaltung nicht verwendet
                            </div>
                          )}
                          <h5 style={{ margin: '0 0 1rem 0', fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', opacity: 0.9 }}>
                            Gesamt-Komponentenpreis
                          </h5>

                          {/* Steueraufschlüsselung */}
                          <div style={{ marginBottom: '1rem' }}>
                            {taxBreakdown.taxBreakdown.map((taxGroup, idx) => (
                              <div key={idx} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '13px',
                                marginBottom: '4px',
                                opacity: 0.95
                              }}>
                                <span>
                                  {(taxGroup.taxRate * 100).toFixed(0)}% MwSt.: {taxGroup.netAmount.toFixed(2)} € (netto) + {taxGroup.taxAmount.toFixed(2)} € (MwSt.)
                                </span>
                                <span style={{ fontWeight: '500' }}>
                                  = {taxGroup.grossAmount.toFixed(2)} € (brutto)
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Summen */}
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '0.75rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                              <span>Gesamt-Komponentenpreis (netto):</span>
                              <span style={{ fontWeight: '600' }}>{taxBreakdown.totalNet.toFixed(2)} €</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                              <span>Gesamt MwSt.:</span>
                              <span style={{ fontWeight: '600' }}>+ {taxBreakdown.totalTax.toFixed(2)} €</span>
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: '16px',
                              fontWeight: '700',
                              paddingTop: '6px',
                              borderTop: '1px solid rgba(255,255,255,0.3)'
                            }}>
                              <span>Gesamt-Komponentenpreis (brutto):</span>
                              <span>{taxBreakdown.totalGross.toFixed(2)} €</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* BRUTTO-FIRST: Preis-Sektion (vereinfacht - Admin gibt immer Bruttopreis ein) */}
                  {serviceFormData.isComposite && calculatedPrice !== null ? (
                    <div className="form-group full-width">
                      <label style={{ fontWeight: 'bold', marginBottom: '1rem', display: 'block', textTransform: 'uppercase', fontSize: '13px', color: '#6b7280' }}>Preisgestaltung</label>

                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '2rem',
                        flexWrap: 'wrap',
                        marginBottom: '1rem'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '250px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            checked={priceMode === 'auto'}
                            onChange={() => {
                              setPriceMode('auto');
                              if (calculatedPrice) {
                                setServiceFormData({...serviceFormData, grossPrice: calculatedPrice});
                              }
                            }}
                          />
                          Automatisch berechnen ({formatPrice(calculatedPrice)})
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '200px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            checked={priceMode === 'manual'}
                            onChange={() => setPriceMode('manual')}
                          />
                          Manuell festlegen
                        </label>
                      </div>

                      {priceMode === 'manual' && (
                        <div>
                          <label>Bruttopreis (€) *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={serviceFormData.grossPrice}
                            onChange={(e) => setServiceFormData({...serviceFormData, grossPrice: parseFloat(e.target.value) || 0})}
                            required
                            min="0.01"
                            style={{ marginTop: '8px' }}
                          />
                          {/* Info-Box mit Netto-Berechnung */}
                          <div style={{
                            backgroundColor: '#d4edda',
                            border: '2px solid #28a745',
                            borderRadius: '8px',
                            padding: '16px',
                            marginTop: '12px'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '12px', color: '#155724', fontSize: '14px' }}>
                              MANUELLER BRUTTOPREIS
                            </div>
                            <div style={{ fontSize: '14px', color: '#333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold' }}>Brutto:</span>
                                <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#155724' }}>
                                  {formatPrice(serviceFormData.grossPrice)}
                                </span>
                              </div>
                              <div style={{ borderTop: '1px solid #c3e6cb', marginTop: '8px', paddingTop: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#666' }}>
                                  <span>Netto (berechnet):</span>
                                  <span>{formatPrice(serviceFormData.grossPrice / (1 + (serviceFormData.taxRate || 0.19)))}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666' }}>
                                  <span>MwSt ({((serviceFormData.taxRate || 0.19) * 100).toFixed(0)}%):</span>
                                  <span>{formatPrice(serviceFormData.grossPrice - (serviceFormData.grossPrice / (1 + (serviceFormData.taxRate || 0.19))))}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {Math.abs(serviceFormData.grossPrice - calculatedPrice) > 0.01 && (
                            <p style={{ fontSize: '12px', color: '#856404', marginTop: '8px' }}>
                              Differenz zum berechneten Preis: {formatPrice(serviceFormData.grossPrice - calculatedPrice)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Bruttopreis (€) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={serviceFormData.grossPrice}
                        onChange={(e) => setServiceFormData({...serviceFormData, grossPrice: parseFloat(e.target.value) || 0})}
                        required
                        min="0.01"
                      />
                      <p style={{ fontSize: '13px', color: '#666', marginTop: '6px' }}>
                        Netto-Preis (berechnet): {formatPrice(serviceFormData.grossPrice / (1 + (serviceFormData.taxRate || 0.19)))}
                        ({((serviceFormData.taxRate || 0.19) * 100).toFixed(0)}% MwSt.)
                      </p>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Preistyp *</label>
                    <select
                      value={serviceFormData.pricingType}
                      onChange={(e) => setServiceFormData({...serviceFormData, pricingType: e.target.value as any})}
                      required
                    >
                      <option value="Fixed">Festpreis</option>
                      <option value="PerPerson">Pro Person</option>
                      <option value="PerGroup">Pro Gruppe</option>
                      <option value="PerDay">Pro Tag</option>
                      <option value="PerNight">Pro Nacht</option>
                    </select>
                  </div>

                  {/* Steuersatz nur anzeigen wenn:
                      - Kein Composite ODER
                      - Composite mit manuellem Preismodus
                      Bei "Automatisch berechnen" wird der Steuersatz durch die Komponenten vorgegeben */}
                  {(!serviceFormData.isComposite || priceMode === 'manual') && (
                    <div className="form-group">
                      <label>Steuersatz</label>
                      <select
                        value={serviceFormData.taxRate}
                        onChange={(e) => setServiceFormData({...serviceFormData, taxRate: parseFloat(e.target.value)})}
                      >
                        <option value="0.19">19% (Standard)</option>
                        <option value="0.07">7% (Ermäßigt)</option>
                        <option value="0">0% (Steuerfrei)</option>
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Min. Menge</label>
                    <input
                      type="number"
                      value={serviceFormData.minQuantityPerBooking || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, minQuantityPerBooking: parseInt(e.target.value) || undefined})}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>Max. Menge</label>
                    <input
                      type="number"
                      value={serviceFormData.maxQuantityPerBooking || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, maxQuantityPerBooking: parseInt(e.target.value) || undefined})}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>Verfügbare Kapazität</label>
                    <input
                      type="number"
                      value={serviceFormData.availableCapacity || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, availableCapacity: parseInt(e.target.value) || undefined})}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>Vorlaufzeit (Stunden)</label>
                    <input
                      type="number"
                      value={serviceFormData.requiredLeadTimeHours || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, requiredLeadTimeHours: parseInt(e.target.value) || undefined})}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Sortierung</label>
                    <input
                      type="number"
                      value={serviceFormData.sortOrder || 1}
                      onChange={(e) => setServiceFormData({...serviceFormData, sortOrder: parseInt(e.target.value)})}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>Anbieter Name</label>
                    <input
                      type="text"
                      value={serviceFormData.providerName || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, providerName: e.target.value})}
                      placeholder="z.B. Franken Coffee Experts"
                    />
                  </div>

                  <div className="form-group">
                    <label>Anbieter Kontakt</label>
                    <input
                      type="text"
                      value={serviceFormData.providerContact || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, providerContact: e.target.value})}
                      placeholder="E-Mail oder Telefon"
                    />
                  </div>

                  <div className="form-group">
                    <label>Anbieter Kosten (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={serviceFormData.providerCost || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, providerCost: parseFloat(e.target.value) || undefined})}
                      min="0"
                    />
                  </div>

                  {/* Checkbox-Gruppe mit responsivem Layout */}
                  <div className="form-group full-width" style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '2rem',
                    flexWrap: 'wrap'
                  }}>
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '200px' }}>
                      <input
                        type="checkbox"
                        checked={serviceFormData.isActive}
                        onChange={(e) => setServiceFormData({...serviceFormData, isActive: e.target.checked})}
                      />
                      Aktiv
                    </label>

                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '200px' }}>
                      <input
                        type="checkbox"
                        checked={serviceFormData.requiresManualConfirmation}
                        onChange={(e) => setServiceFormData({...serviceFormData, requiresManualConfirmation: e.target.checked})}
                      />
                      Manuelle Bestätigung erforderlich
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {selectedService ? 'Speichern' : 'Service anlegen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowServiceModal(false)}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Add Composition Dialog */}
      {showCompositionDialog && selectedService && (
        <AddCompositionDialog
          open={showCompositionDialog}
          onClose={() => setShowCompositionDialog(false)}
          onAdd={handleAddComposition}
          existingCompositions={compositions.map(c => c.childAddOnId)}
          parentAddOnId={selectedService.id}
        />
      )}

      {/* Service Details Modal */}
      {showServiceDetails && selectedService && (
        <div className="modal-overlay" onClick={() => setShowServiceDetails(false)}>
          <div className="modal-content customer-details" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background: '#2d3748', padding: '1rem' }}>
              <h3 style={{ color: 'white', margin: 0 }}>Service Details - {selectedService.name}</h3>
              <button onClick={() => setShowServiceDetails(false)} className="btn-close" style={{ color: 'white' }}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-section">
                  <h4>Grundinformationen</h4>
                  <p><strong>Name:</strong> <span>{selectedService.name}</span></p>
                  <p><strong>Beschreibung:</strong> <span>{selectedService.description}</span></p>
                  <p><strong>Kategorie:</strong> <span>{activeCategory?.name}</span></p>
                  <p><strong>Status:</strong> <span>{selectedService.isActive ? 'Aktiv' : 'Inaktiv'}</span></p>
                  {selectedService.isComposite && (
                    <p><strong>Typ:</strong> <span style={{
                      background: '#3b82f6',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontWeight: '600',
                      display: 'inline-block'
                    }}>🔷 Composite Add-on</span></p>
                  )}
                </div>

                <div className="detail-section">
                  <h4>Preise & Konditionen</h4>
                  <p><strong>Bruttopreis:</strong> <span style={{ fontWeight: 'bold', color: '#155724' }}>{formatPrice(selectedService.grossPrice)}</span></p>
                  <p><strong>Nettopreis:</strong> <span>{formatPrice(selectedService.price)}</span></p>
                  <p><strong>Preistyp:</strong> <span>{getPricingTypeLabel(selectedService.pricingType)}</span></p>
                  <p><strong>Steuersatz:</strong> <span>{(selectedService.taxRate * 100).toFixed(0)}%</span></p>
                  {selectedService.providerCost && (
                    <p><strong>Anbieter Kosten:</strong> <span>{selectedService.providerCost.toFixed(2)} € (Marge: {(selectedService.price - selectedService.providerCost).toFixed(2)} €)</span></p>
                  )}
                </div>

                {/* NEW: Composite Add-on Components with Tax Breakdown */}
                {selectedService.isComposite && taxBreakdown && taxBreakdown.taxBreakdown.length > 0 && (
                  <div className="detail-section full-width">
                    <h4>Komponenten mit Steueraufschlüsselung ({taxBreakdown.components.length})</h4>

                    {/* Tax Groups */}
                    {taxBreakdown.taxBreakdown.map((taxGroup, groupIndex) => (
                      <div key={groupIndex} style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '12px',
                        marginTop: groupIndex === 0 ? '8px' : '12px'
                      }}>
                        <div style={{
                          fontWeight: '600',
                          marginBottom: '8px',
                          color: '#1e40af',
                          fontSize: '0.95em'
                        }}>
                          {(taxGroup.taxRate * 100).toFixed(0)}% MwSt. Gruppe
                        </div>

                        {/* Components in this tax group */}
                        {taxGroup.components.map((comp, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 0',
                            borderBottom: index < taxGroup.components.length - 1 ? '1px solid #e2e8f0' : 'none',
                            fontSize: '0.9em'
                          }}>
                            <span>
                              <strong>{comp.quantity}x</strong> {comp.childName}
                            </span>
                            <span style={{ color: '#64748b' }}>
                              {comp.childPrice.toFixed(2)} € × {comp.quantity} = <strong>{comp.totalPrice.toFixed(2)} €</strong>
                            </span>
                          </div>
                        ))}

                        {/* Tax group summary */}
                        <div style={{
                          marginTop: '8px',
                          paddingTop: '8px',
                          borderTop: '1px solid #cbd5e1',
                          fontSize: '0.9em'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span>Nettobetrag ({(taxGroup.taxRate * 100).toFixed(0)}%):</span>
                            <span><strong>{taxGroup.netAmount.toFixed(2)} €</strong></span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#64748b' }}>
                            <span>MwSt. ({(taxGroup.taxRate * 100).toFixed(0)}%):</span>
                            <span>+ {taxGroup.taxAmount.toFixed(2)} €</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600', color: '#1e40af' }}>
                            <span>Bruttobetrag:</span>
                            <span>{taxGroup.grossAmount.toFixed(2)} €</span>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Grand Total */}
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      background: '#1e40af',
                      color: 'white',
                      borderRadius: '6px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.9em' }}>
                        <span>Gesamt Netto:</span>
                        <span>{taxBreakdown.totalNet.toFixed(2)} €</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.9em' }}>
                        <span>Gesamt MwSt.:</span>
                        <span>+ {taxBreakdown.totalTax.toFixed(2)} €</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1em', fontWeight: '600', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                        <span>Gesamt Brutto:</span>
                        <span>{taxBreakdown.totalGross.toFixed(2)} €</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="detail-section">
                  <h4>Kapazitäten</h4>
                  {selectedService.minQuantityPerBooking && (
                    <p><strong>Min. Menge:</strong> <span>{selectedService.minQuantityPerBooking}</span></p>
                  )}
                  {selectedService.maxQuantityPerBooking && (
                    <p><strong>Max. Menge:</strong> <span>{selectedService.maxQuantityPerBooking}</span></p>
                  )}
                  {selectedService.availableCapacity && (
                    <p><strong>Verfügbar:</strong> <span>{selectedService.availableCapacity}</span></p>
                  )}
                  {selectedService.requiredLeadTimeHours && (
                    <p><strong>Vorlaufzeit:</strong> <span>{selectedService.requiredLeadTimeHours} Stunden</span></p>
                  )}
                </div>

                {/* NEW: Service Details (Duration, Setup, Cleanup) */}
                {(selectedService.serviceDuration || selectedService.setupTime || selectedService.cleanupTime) && (
                  <div className="detail-section">
                    <h4>Service-Zeiten</h4>
                    {selectedService.serviceDuration && (
                      <p><strong>Leistungsdauer:</strong> <span>{selectedService.serviceDuration}</span></p>
                    )}
                    {selectedService.setupTime && (
                      <p><strong>Aufbauzeit:</strong> <span>{selectedService.setupTime}</span></p>
                    )}
                    {selectedService.cleanupTime && (
                      <p><strong>Abbauzeit:</strong> <span>{selectedService.cleanupTime}</span></p>
                    )}
                  </div>
                )}

                {selectedService.providerName && (
                  <div className="detail-section">
                    <h4>Anbieter</h4>
                    <p><strong>Name:</strong> <span>{selectedService.providerName}</span></p>
                    {selectedService.providerContact && (
                      <p><strong>Kontakt:</strong> <span>{selectedService.providerContact}</span></p>
                    )}
                  </div>
                )}

                {/* NEW: Delivery & Cancellation Information */}
                {(selectedService.deliveryInstructions || selectedService.cancellationPolicy) && (
                  <div className="detail-section full-width">
                    <h4>Zusätzliche Informationen</h4>
                    {selectedService.deliveryInstructions && (
                      <p><strong>Lieferhinweise:</strong> <span>{selectedService.deliveryInstructions}</span></p>
                    )}
                    {selectedService.cancellationPolicy && (
                      <p><strong>Stornierungsbedingungen:</strong> <span>{selectedService.cancellationPolicy}</span></p>
                    )}
                  </div>
                )}

                <div className="detail-section full-width">
                  <h4>Optionen</h4>
                  <p><strong>Manuelle Bestätigung:</strong> <span>{selectedService.requiresManualConfirmation ? 'Ja' : 'Nein'}</span></p>
                  <p><strong>Sortierung:</strong> <span>{selectedService.sortOrder}</span></p>
                </div>

                {/* NEW: Timestamps */}
                {(selectedService.createdAt || selectedService.updatedAt) && (
                  <div className="detail-section full-width">
                    <h4>Zeitstempel</h4>
                    {selectedService.createdAt && (
                      <p><strong>Erstellt am:</strong> <span>{new Date(selectedService.createdAt).toLocaleString('de-DE')}</span></p>
                    )}
                    {selectedService.updatedAt && (
                      <p><strong>Zuletzt aktualisiert:</strong> <span>{new Date(selectedService.updatedAt).toLocaleString('de-DE')}</span></p>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  onClick={async () => {
                    setShowServiceDetails(false);
                    setServiceFormData({
                      categoryId: selectedService.categoryId || activeCategoryTab!,
                      name: selectedService.name,
                      description: selectedService.description,
                      grossPrice: selectedService.grossPrice, // BRUTTO-FIRST
                      pricingType: selectedService.pricingType,
                      taxRate: selectedService.taxRate,
                      isActive: selectedService.isActive,
                      sortOrder: selectedService.sortOrder,
                      minQuantityPerBooking: selectedService.minQuantityPerBooking,
                      maxQuantityPerBooking: selectedService.maxQuantityPerBooking,
                      availableCapacity: selectedService.availableCapacity,
                      requiresManualConfirmation: selectedService.requiresManualConfirmation,
                      requiredLeadTimeHours: selectedService.requiredLeadTimeHours,
                      providerName: selectedService.providerName,
                      providerContact: selectedService.providerContact,
                      providerCost: selectedService.providerCost,
                      isComposite: selectedService.isComposite || false
                    });

                    // Load compositions for composite add-ons
                    if (selectedService.isComposite && selectedService.id) {
                      try {
                        const comps = await adminService.getCompositions(selectedService.id);
                        setCompositions(comps);

                        // Load calculated price
                        const calcPrice = await adminService.getCalculatedPrice(selectedService.id);
                        setCalculatedPrice(calcPrice.calculatedPrice);

                        // Set price mode based on whether price matches calculated price
                        if (Math.abs(selectedService.price - calcPrice.calculatedPrice) < 0.01) {
                          setPriceMode('auto');
                        } else {
                          setPriceMode('manual');
                        }
                      } catch (error) {
                        console.error('Failed to load compositions for edit:', error);
                        setCompositions([]);
                        setCalculatedPrice(null);
                        setPriceMode('manual');
                      }
                    } else {
                      setCompositions([]);
                      setCalculatedPrice(null);
                      setPriceMode('manual');
                    }

                    setIsEditMode(true);
                    setShowServiceModal(true);
                  }}
                  className="btn-primary"
                >
                  Bearbeiten
                </button>
                <button onClick={() => setShowServiceDetails(false)} className="btn-secondary">
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOnManagement;
